import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { sanitizeMinimalHtml } from '../helpers/sanitize';
const GET_REDEMPTION_BENEFIT = gql `
  query GetRedemptionBenefit($id: ID!) {
    benefit(id: $id) {
      __typename
      id
      name
      ... on RedemptionBenefit {
        delivery
        perPeriod
        shippingNotesInstructions
      }
    }
  }
`;
const deliveryOptions = [
    { value: 'ANNUAL', label: 'Every year' },
    { value: 'QUARTERLY', label: 'Every three months' },
    { value: 'MONTHLY', label: 'Every month' },
    { value: 'BIWEEKLY', label: 'Every two weeks' },
    { value: 'WEEKLY', label: 'Every week' },
    { value: 'ONCE', label: 'Entire membership (no reset)' },
];
const RedemptionEditor = ({ id }) => {
    var _a, _b;
    const client = useApolloClient();
    const { data } = useQuery(GET_REDEMPTION_BENEFIT, { variables: { id } });
    const redemption = ((_a = data === null || data === void 0 ? void 0 : data.benefit) === null || _a === void 0 ? void 0 : _a.__typename) === 'RedemptionBenefit' ? data.benefit : null;
    const nameRef = useRef(null);
    const [isNameFocused, setIsNameFocused] = useState(false);
    const write = useCallback((patch) => {
        var _a, _b, _c, _d;
        if (!redemption)
            return;
        client.writeQuery({
            query: GET_REDEMPTION_BENEFIT,
            variables: { id },
            data: {
                benefit: {
                    __typename: 'RedemptionBenefit',
                    id: redemption.id,
                    name: (_a = patch.name) !== null && _a !== void 0 ? _a : redemption.name,
                    delivery: (_b = patch.delivery) !== null && _b !== void 0 ? _b : redemption.delivery,
                    perPeriod: (_c = patch.perPeriod) !== null && _c !== void 0 ? _c : redemption.perPeriod,
                    shippingNotesInstructions: (_d = patch.shippingNotesInstructions) !== null && _d !== void 0 ? _d : redemption.shippingNotesInstructions,
                },
            },
        });
    }, [client, id, redemption]);
    // Keep the DOM in sync when not focused; avoid resetting caret while typing
    useEffect(() => {
        if (!redemption || !nameRef.current)
            return;
        if (isNameFocused)
            return;
        nameRef.current.innerHTML = sanitizeMinimalHtml(redemption.name || '');
    }, [redemption === null || redemption === void 0 ? void 0 : redemption.id, redemption === null || redemption === void 0 ? void 0 : redemption.name, isNameFocused]);
    if (!redemption)
        return null;
    return (_jsxs("div", { className: "w-full max-w-lg px-4", children: [_jsx("h1", { className: "text-2xl my-4", children: "Redemption Benefit Settings" }), _jsxs("div", { className: "my-4", children: [_jsx("label", { className: "block mb-2", children: "Benefit name" }), _jsx("div", { ref: nameRef, contentEditable: true, suppressContentEditableWarning: true, className: "p-2 w-full border border-wfGray-300 focus:border-salmon-600 focus:border-2 min-h-[42px]", onInput: () => {
                            var _a;
                            const html = ((_a = nameRef.current) === null || _a === void 0 ? void 0 : _a.innerHTML) || '';
                            write({ name: sanitizeMinimalHtml(html) });
                        }, onKeyDown: (e) => {
                            if (e.key === 'Enter')
                                e.preventDefault();
                        }, onFocus: () => setIsNameFocused(true), onBlur: () => setIsNameFocused(false) })] }), _jsxs("div", { className: "my-4", children: [_jsx("label", { className: "block mb-2", children: "Period" }), _jsx("p", { className: "text-sm text-wfGray-600 mb-2", children: "How often the redemption allowance resets for each member." }), _jsx("select", { className: "p-2 w-full border border-wfGray-300 focus:border-salmon-600 focus:border-2", value: redemption.delivery, onChange: (e) => write({ delivery: e.target.value }), children: deliveryOptions.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) })] }), _jsxs("div", { className: "my-4", children: [_jsx("label", { className: "block mb-2", children: "Redemptions per period" }), _jsx("p", { className: "text-sm text-wfGray-600 mb-2", children: "How many times a member can redeem this benefit during each period." }), _jsx("input", { type: "number", min: 1, className: "p-2 w-full border border-wfGray-300 focus:border-salmon-600 focus:border-2", value: (_b = redemption.perPeriod) !== null && _b !== void 0 ? _b : 1, onChange: (e) => write({ perPeriod: Number(e.target.value) }), required: true })] }), _jsxs("div", { className: "my-4", children: [_jsx("label", { className: "block mb-2", children: "Notes (optional)" }), _jsx("p", { className: "text-sm text-wfGray-600 mb-2", children: "A question that staff can note with each redemption (not shown to customers). For example, for \u201C1 free ARC per month,\u201D you might ask which book they picked up." }), _jsx("textarea", { className: "p-2 w-full min-h-[100px] border border-wfGray-300 focus:border-salmon-600 focus:border-2", value: redemption.shippingNotesInstructions || '', onChange: (e) => write({ shippingNotesInstructions: e.target.value }), placeholder: "e.g. Which book did they get?" })] })] }));
};
export default RedemptionEditor;
